import { alpha } from "@mui/material"
import { blue, blueGrey, lightBlue } from "./palette"

// single source of truth for focus. FOCUS_COLOR feeds palette.focus; the
// focusRing object feeds theme.mixins.focusRing and the global :focus-visible
// styles in both themes. 1px solid + 3px translucent box-shadow, per figma.
export const FOCUS_COLOR = blue["A700"]

// White ring for dark surfaces (banners, dark nav), where the blue ring fails
// a11y: 3:1 contrast. WCAG 1.4.11 needs the indicator to contrast its own surface.
export const FOCUS_COLOR_ON_DARK = "#FFFFFF"

const ringBoxShadow = (color: string) =>
  `0px 0px 0px 1px ${color}, 0px 0px 0px 3px ${alpha(color, 0.2)}`

// a11y: box-shadow is dropped in forced-colors/High Contrast mode, so the
// box-shadow-only variants (ring, shadow) need a real outline fallback there.
const forcedColorsOutline = {
  "@media (forced-colors: active)": {
    outline: "2px solid CanvasText",
    outlineOffset: "1px"
  }
}

export const createFocusRing = (color: string) => {
  const boxShadow = ringBoxShadow(color)
  return {
    ring: {
      borderColor: color,
      outline: "none",
      boxShadow,
      ...forcedColorsOutline
    },
    shadow: { outline: "none", boxShadow, ...forcedColorsOutline },
    outline: { borderColor: color, outline: `3px solid ${alpha(color, 0.2)}` },
    // ring pushed off the element by a 2px white gap so it reads against the light page, not a
    // filled/colored element it sits on (e.g. the teal selected nav item on a light nav).
    ringWithGap: {
      outline: "none",
      boxShadow: `0px 0px 0px 2px #FFFFFF, 0px 0px 0px 3px ${color}, 0px 0px 0px 5px ${alpha(
        color,
        0.2
      )}`,
      ...forcedColorsOutline
    }
  }
}

export const focusRing = {
  ...createFocusRing(FOCUS_COLOR),
  // Light-surface focus tint (e.g. ActionButton); omitted from onDark where a blue wash won't read.
  backgroundColor: alpha(lightBlue[100], 0.5)
}
export const focusRingOnDark = createFocusRing(FOCUS_COLOR_ON_DARK)

// Two-tone ring for VARIABLE / branded surfaces (e.g. subject-gradient banners) where no
// single ring color holds 3:1 across the whole background: a 1px dark inner line carries the
// light banner areas + the near-white button edge, a 1px white ring carries the dark areas,
// plus a soft translucent halo. Verified >=3:1 on every WorkKeys course banner.
export const FOCUS_COLOR_TWO_TONE_DARK = blueGrey[900]

const twoToneBoxShadow = `0px 0px 0px 1px ${FOCUS_COLOR_TWO_TONE_DARK}, 0px 0px 0px 2px ${FOCUS_COLOR_ON_DARK}, 0px 0px 0px 4px ${alpha(
  FOCUS_COLOR_ON_DARK,
  0.2
)}`

export const focusRingTwoTone = {
  ring: {
    outline: "none",
    boxShadow: twoToneBoxShadow,
    ...forcedColorsOutline
  }
}
